import { useRecipeHistoryService } from "../../Hooks/useRecipeHistoryService";
import { useRecipeService } from "../../Hooks/useRecipeService";
import { PageHeader } from "../../Shared/PageHeader";
import PageOverlay from "../../Shared/PageOverlay";
import React, { useState, useEffect, useRef } from "react";
import { Paper, Typography, Button, Divider } from "@material-ui/core";
import { Link } from "react-router-dom";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import * as Diff from "diff";
import "./RecipeCompare.scss";

export function RecipeCompare(props) {
	const recipeHistoryService = useRecipeHistoryService(props.config);
	const recipeService = useRecipeService(props.config);
	const [recipe1, setRecipe1] = useState(null);
	const [recipe2, setRecipe2] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const loadKeyRef = useRef("");

	const recipeId = props.match.params.recipeId;
	const id1 = props.match.params.id1;
	const id2 = props.match.params.id2;

	useEffect(() => {
		const loadKey = `${recipeId}-${id1}-${id2}`;
		if (loadKeyRef.current === loadKey) {
			return;
		}
		loadKeyRef.current = loadKey;

		let isCancelled = false;
		document.title = `Compare Versions - ${props.config.appName}`;
		setIsLoading(true);
		setRecipe1(null);
		setRecipe2(null);

		const normalizeHistoryId = (versionId) => {
			if (versionId === "current") {
				return versionId;
			}
			const parsed = Number.parseInt(versionId, 10);
			return Number.isNaN(parsed) ? versionId : parsed;
		};

		const markLoaded = (() => {
			let leftLoaded = false;
			let rightLoaded = false;
			return (side) => {
				if (side === "left") {
					leftLoaded = true;
				}
				if (side === "right") {
					rightLoaded = true;
				}
				if (leftLoaded && rightLoaded && !isCancelled) {
					setIsLoading(false);
				}
			};
		})();

		const loadVersion = (versionId, side, onLoaded) => {
			if (versionId === "current") {
				recipeService.getRecipeById(recipeId, "raw=1", (response) => {
					if (!isCancelled && response && response.data) {
						onLoaded({
							...response.data,
							isCurrent: true,
							changeReason: response.data.changeReason || "N/A",
							historyDateTime: response.data.updateDateTime
						});
						markLoaded(side);
					}
				}, (error) => {
					if (!isCancelled) {
						console.error(error);
						markLoaded(side);
					}
				});
				return;
			}

			recipeHistoryService.getHistoryEntry(versionId, (response) => {
				if (!isCancelled && response && response.data) {
					onLoaded({
						...response.data,
						isCurrent: false,
						historyDateTime: response.data.recipeHistoryDateTime
					});
					markLoaded(side);
				}
			}, (error) => {
				if (!isCancelled) {
					console.error(error);
					markLoaded(side);
				}
			});
		};

		const leftId = normalizeHistoryId(id1);
		const rightId = normalizeHistoryId(id2);

		loadVersion(leftId, "left", (data) => {
			if (!isCancelled) {
				setRecipe1(data);
			}
		});
		loadVersion(rightId, "right", (data) => {
			if (!isCancelled) {
				setRecipe2(data);
			}
		});

		return () => {
			isCancelled = true;
		};
	}, [id1, id2, recipeId]); // eslint-disable-line react-hooks/exhaustive-deps

	const formatDateTime = (dateTimeString) => {
		if (!dateTimeString) return "";
		const date = new Date(dateTimeString);
		return date.toLocaleString();
	};

	const renderDiffLines = (leftText, rightText, side) => {
		const normalizeText = (text) => {
			if (!text) return "";
			return text.toString().replace(/\r\n/g, "\n").replace(/\r/g, "\n");
		};

		const splitLines = (text) => {
			if (!text) return [];
			const normalized = normalizeText(text);
			const lines = normalized.split("\n");
			if (normalized.endsWith("\n")) {
				lines.pop();
			}
			return lines;
		};

		const leftLinesSrc = splitLines(leftText);
		const rightLinesSrc = splitLines(rightText);
		const diff = Diff.diffArrays(leftLinesSrc, rightLinesSrc);
		const aligned = [];

		for (let index = 0; index < diff.length; index++) {
			const part = diff[index];
			if (part.removed && index + 1 < diff.length && diff[index + 1].added) {
				const addedPart = diff[index + 1];
				const removedLines = part.value;
				const addedLines = addedPart.value;
				
				// Pair up corresponding lines for character-level diffs
				const minLen = Math.min(removedLines.length, addedLines.length);
				
				// Pair lines up to the minimum count
				for (let i = 0; i < minLen; i++) {
					aligned.push({
						left: { text: removedLines[i], isChanged: true },
						right: { text: addedLines[i], isChanged: true }
					});
				}
				
				// Add any remaining lines as pure additions or removals
				for (let i = minLen; i < removedLines.length; i++) {
					aligned.push({
						left: { text: removedLines[i], isChanged: true },
						right: { text: "", isEmpty: true }
					});
				}
				for (let i = minLen; i < addedLines.length; i++) {
					aligned.push({
						left: { text: "", isEmpty: true },
						right: { text: addedLines[i], isChanged: true }
					});
				}
				
				index++; // Skip the added part since we processed it
				continue;
			}
			if (part.added) {
				part.value.forEach(line => {
					aligned.push({
						left: { text: "", isEmpty: true },
						right: { text: line, isChanged: true }
					});
				});
			} else if (part.removed) {
				part.value.forEach(line => {
					aligned.push({
						left: { text: line, isChanged: true },
						right: { text: "", isEmpty: true }
					});
				});
			} else {
				part.value.forEach(line => {
					aligned.push({
						left: { text: line, isChanged: false },
						right: { text: line, isChanged: false }
					});
				});
			}
		}

		const linesToRender = aligned.map((pair) => (side === "left" ? pair.left : pair.right));
		return linesToRender.map((line, index) => {
			const key = `${side}-${index}`;
			const otherLine = aligned[index][side === "left" ? "right" : "left"];
			if (line.isEmpty) {
				return (
					<div key={key} className="recipe-diff-line recipe-diff-line--empty">
						{"\u00A0"}
					</div>
				);
			}
			return (
				<div key={key} className={`recipe-diff-line${line.isChanged ? " recipe-diff-line--changed" : ""}`}>
					{renderLineText(line.text ?? "", otherLine, side)}
				</div>
			);
		});
	};

	const renderLineText = (text, otherLine, side) => {
		const otherText = otherLine?.text ?? "";
		if (text === "" && otherText === "") {
			return "\u00A0";
		}
		if (!otherText || !text || !otherLine) {
			return text || "\u00A0";
		}
		
		// Always compare left to right to keep semantics consistent
		const leftText = side === "left" ? text : otherText;
		const rightText = side === "left" ? otherText : text;
		const segments = Diff.diffChars(leftText, rightText);
		
		return segments
			.filter((segment) => {
				if (side === "left") {
					// Left side: show removed and unchanged
					return !segment.added;
				} else {
					// Right side: show added and unchanged
					return !segment.removed;
				}
			})
			.map((segment, index) => (
				<span
					key={`char-${index}`}
					className={segment.added || segment.removed ? "recipe-diff-char--changed" : undefined}
				>
					{segment.value}
				</span>
			));
	};

	const renderRecipeColumn = (recipe, otherRecipe, side) => {
		if (!recipe) {
			return (
				<div>
					<Typography>Loading...</Typography>
				</div>
			);
		}

		const changeBy = recipe.username || recipe.updateUsername || 'Unknown';
		const dateLabel = `${formatDateTime(recipe.historyDateTime)}${recipe.isCurrent ? " (Current)" : ""}`;
		const versionLabel = recipe.isCurrent ? "Current" : recipe.recipeHistoryId;

		const leftRecipe = side === "left" ? recipe : otherRecipe;
		const rightRecipe = side === "left" ? otherRecipe : recipe;

		return (
			<div className="recipe-compare-column">
				<Typography variant="subtitle2" color="textSecondary">
					Version: {versionLabel}
				</Typography>
				<Typography variant="subtitle2" color="textSecondary">
					Version Date: {dateLabel}
				</Typography>
				<Typography variant="subtitle2" color="textSecondary" gutterBottom>
					Changed By: {changeBy}
				</Typography>
				<Typography variant="subtitle2" color="textSecondary" gutterBottom>
					Change Reason: {recipe.changeReason || 'N/A'}
				</Typography>

				<Divider style={{ margin: '16px 0' }} />

				<Typography variant="h6" gutterBottom>Name</Typography>
				<div className="recipe-compare-text rb-recipe-info-body">
					{renderDiffLines(leftRecipe?.name, rightRecipe?.name, side)}
				</div>

				<Divider style={{ margin: '16px 0' }} />

				<Typography variant="h6" gutterBottom>Ingredients</Typography>
				<div className="recipe-compare-text rb-recipe-info-body">
					{renderDiffLines(leftRecipe?.ingredients, rightRecipe?.ingredients, side)}
				</div>

				<Divider style={{ margin: '16px 0' }} />

				<Typography variant="h6" gutterBottom>Instructions</Typography>
				<div className="recipe-compare-text rb-recipe-info-body">
					{renderDiffLines(leftRecipe?.instructions, rightRecipe?.instructions, side)}
				</div>

				<Divider style={{ margin: '16px 0' }} />

				<Typography variant="h6" gutterBottom>Notes</Typography>
				<div className="recipe-compare-text rb-recipe-info-body">
					{renderDiffLines(leftRecipe?.notes, rightRecipe?.notes, side)}
				</div>
			</div>
		);
	};

	return (
		<React.Fragment>
			<PageHeader text="Compare Recipe Versions" config={props.config} />
			<PageOverlay showOverlay={isLoading} />
			<Paper style={{ padding: 12 }}>
				<div style={{ marginBottom: 20 }}>
					<Button
						component={Link}
						to={`/recipes/${recipeId}/history`}
						variant="contained"
						color="primary">
						<ArrowBackIcon style={{ marginRight: 8 }} />
						Back to History
					</Button>
				</div>
				<div style={{ display: "flex", gap: 12, flexWrap: "nowrap" }}>
					<div style={{ flex: 1, minWidth: 0 }} key={`compare-left-${id1}`}>
						{renderRecipeColumn(recipe1, recipe2, "left")}
					</div>
					<div style={{ flex: 1, minWidth: 0 }} key={`compare-right-${id2}`}>
						{renderRecipeColumn(recipe2, recipe1, "right")}
					</div>
				</div>
			</Paper>
		</React.Fragment>
	);
}
